import { useStripe } from "@stripe/react-stripe-js";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useStripeSubscription } from "~/api/billing/queries";
import { ISubscriptionStripe } from "~/api/billing/types";
import handleErrors from "~/helpers/handleErrors";
import { IPaymentValues } from "~/types/billing";
import { useShopifyPayment } from "./useShopifyPayment";
import { toast } from "~/components/toast";
import { useUTM } from "~/hooks/useUTM";
import { UAParser } from "ua-parser-js";
import { getUserAgentData } from "~/helpers/userAgentData";
import { getMetaBrowserIds } from "~/helpers/clickIds";
import { IActiveSubscription } from "~/types/account";

type Props = {
  planId: string;
  isUserShopify: boolean;
};

export const usePayment = ({ planId, isUserShopify }: Props) => {
  const { mutateAsync: stripeSubscription } = useStripeSubscription();
  const { t } = useTranslation();
  const stripe = useStripe();
  const payShopify = useShopifyPayment(planId);
  const utms = useUTM();
  const parser = new UAParser();
  const lastActiveSubscriptionRef = useRef<IActiveSubscription | null>(null);

  const payStripe = async (values?: IPaymentValues) => {
    const payload: ISubscriptionStripe = { plan_id: planId };
    lastActiveSubscriptionRef.current = null;
    if (values?.name) payload.name = values?.name;
    if (values?.payment_method_id)
      payload.payment_method_id = values?.payment_method_id;
    if (values?.setup_intent) payload.setup_intent = values?.setup_intent;
    if (values?.promo_code_id) payload.promo_code_id = values?.promo_code_id;
    try {
      const hasAttribution =
        !!utms.campaign ||
        !!utms.source ||
        !!utms.medium ||
        !!utms.term ||
        !!utms.gclid ||
        !!utms.gbraid ||
        !!utms.wbraid ||
        !!utms.msclkid ||
        !!utms.fbclid ||
        !!utms.oppref;

      const utmParams: { [key: string]: string | null } = hasAttribution
        ? utms
        : {};

      const response = await stripeSubscription({
        ...payload,
        utms: {
          ...utmParams,
          // Meta's browser ID is the browser's, not part of a touch.
          ...getMetaBrowserIds(),
          ...getUserAgentData(parser),
        },
      });

      if (response?.active_subscription) {
        lastActiveSubscriptionRef.current = response.active_subscription;
      }

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("plan_id", planId);
      searchParams.delete("annual");
      searchParams.delete("annual-plans-modal");
      let returnURL = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;

      if (response?.client_secret && stripe) {
        // Stripe confirms this and then reloads the page at return_url, the same
        // flow as the other apps. useStripeConfirmation shows the loading screen
        // on that return until the customer is sent into the app.
        const confirmPaymentResponse = await stripe.confirmPayment({
          clientSecret: response.client_secret,
          confirmParams: {
            return_url: returnURL,
          },
        });

        if (confirmPaymentResponse.error) {
          toast.warning(
            confirmPaymentResponse.error.message ?? t("checkout.went_wrong")
          );
          return confirmPaymentResponse?.error;
        }

        return null;
      }
    } catch (err) {
      // The API returns {payment_method_id: [message]} (e.g. "Your previous
      // payment is still being processed..."). It used to be dropped here, so
      // every refusal showed as a bare "Something went wrong".
      const data = (
        err as { response?: { data?: Record<string, unknown> } } | undefined
      )?.response?.data;
      const paymentMethodErrors = data?.payment_method_id;
      const apiMessage = Array.isArray(paymentMethodErrors)
        ? paymentMethodErrors[0]
        : data?.message || data?.error;
      toast.warning(
        typeof apiMessage === "string" && apiMessage
          ? apiMessage
          : t("checkout.went_wrong")
      );
      return "error";
    }

    return null;
  };

  const pay = async (values?: IPaymentValues) => {
    if (isUserShopify) {
      return payShopify();
    }

    const toastMessages = {
      loading: t("checkout.loading"),
      success: t("checkout.success"),
      error: t("checkout.error"),
    };
    const { response } = await handleErrors(
      () => payStripe(values),
      toastMessages
    );
    return response;
  };

  return {
    pay,
    payStripe,
    payShopify,
    lastActiveSubscription: lastActiveSubscriptionRef,
  };
};
